/* eslint-disable import/no-unresolved */
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import heroImage from '../../images/car-inspection-service-hero.jpg';

export default function CarInspectionServicePage() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What car inspection services does Focus Motors offer?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Focus Motors offers two main car inspection services — Pre-Purchase Car Inspection for anyone buying a new or used car, and Used Car Inspection focused specifically on detecting hidden issues in pre-owned vehicles.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much does a car inspection cost in Kerala?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Focus Motors offers a Normal Inspection at Rs.4,999 and a Premium Inspection at Rs.7,999. The right package depends on the car type and buyer needs.',
        },
      },
      {
        '@type': 'Question',
        name: 'Where are car inspection services available?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Car inspection services are available across all of Kerala. Our inspector visits the car location directly.',
        },
      },
      {
        '@type': 'Question',
        name: 'How long does a car inspection take?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most inspections are completed within 1-2 hours, with a detailed report delivered shortly after.',
        },
      },
    ],
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Car Inspection Service',
    provider: { '@type': 'Organization', name: 'Focus Motors', url: 'https://focusmotors.in' },
    areaServed: { '@type': 'State', name: 'Kerala' },
    url: 'https://focusmotors.in/car-inspection-service',
    description:
      'Professional car inspection services in Kerala, including pre-purchase inspection and used car inspection. 130+ point checks by certified inspectors.',
    offers: [
      {
        '@type': 'Offer',
        name: 'Normal Inspection',
        price: '4999',
        priceCurrency: 'INR',
        url: 'https://focusmotors.in/inspection-booking',
      },
      {
        '@type': 'Offer',
        name: 'Premium Inspection',
        price: '7999',
        priceCurrency: 'INR',
        url: 'https://focusmotors.in/inspection-booking',
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Car Inspection Service in Kerala | Focus Motors</title>
        <meta
          name="description"
          content="Professional car inspection services across Kerala — pre-purchase inspection and used car inspection. 130+ point checks. Normal Rs.4,999 | Premium Rs.7,999."
        />
        <meta
          name="keywords"
          content="car inspection service, car inspection Kerala, pre-purchase car inspection, used car inspection service Kerala, vehicle inspection service"
        />
        <link rel="canonical" href="https://focusmotors.in/inspection" />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content="Car Inspection Service in Kerala | Focus Motors" />
        <meta
          property="og:description"
          content="Professional car inspection services across Kerala — pre-purchase and used car inspection. 130+ point checks."
        />
        <meta property="og:url" content="https://focusmotors.in/car-inspection-service" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <div
        style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px', fontFamily: 'inherit', color: '#ffffff' }}
      >
        <img
          src={heroImage}
          alt="Professional car inspection service in Kerala"
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: '450px',
            objectFit: 'cover',
            borderRadius: '12px',
            marginBottom: '30px',
          }}
        />

        <h1>Car Inspection Service in Kerala</h1>
        <p>
          Focus Motors offers professional car inspection services across Kerala, helping buyers and owners make
          informed decisions with a thorough, independent 130+ point check.
        </p>

        <h2>Our Inspection Services</h2>

        <div
          style={{ marginBottom: '20px', padding: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}
        >
          <h3>Pre-Purchase Car Inspection</h3>
          <p>
            Planning to buy a car? Get a full inspection before you pay — engine, brakes, electricals, body condition,
            and more.
        </p>
          <Link to="/inspection" style={{ color: '#ffa500' }}>
           Learn more about Pre-Purchase Car Inspection →
        </Link>
        </div>

        <div
          style={{ marginBottom: '20px', padding: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}
        >
          <h3>Used Car Inspection</h3>
          <p>
            Buying a used car? Detect hidden accident history, odometer tampering, and engine issues before you buy.
          </p>
          <a href="https://focusmotors.in/used-car-inspection-kerala" style={{ color: '#ffa500' }}>
            Learn more about Used Car Inspection →
          </a>
        </div>

        <h2>Inspection Packages</h2>
        <p>
          <strong>Normal Inspection — Rs.4,999</strong>
          <br />
          For normal cars. Complete 130+ point inspection.
        </p>
        <p>
          <strong>Premium Inspection — Rs.7,999</strong>
          <br />
          For premium cars. Same 130+ point inspection.
        </p>
        <a
          href="https://focusmotors.in/inspection-booking"
          style={{
            display: 'inline-block',
            marginTop: '10px',
            padding: '12px 24px',
            background: '#000',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: '6px',
          }}
        >
          Book an Inspection
        </a>

        <h2>Available Across Kerala</h2>
        <p>
          Our car inspection services cover all of Kerala — Kochi, Thrissur, Kozhikode, Thiruvananthapuram, and more.
          Our inspector comes to the car's location.
        </p>

        <h2>Frequently Asked Questions</h2>

        <h3>What car inspection services does Focus Motors offer?</h3>
        <p>Focus Motors offers Pre-Purchase Car Inspection and Used Car Inspection, both with a 130+ point check.</p>

        <h3>How much does a car inspection cost in Kerala?</h3>
        <p>Normal Inspection at Rs.4,999 for normal cars and Premium Inspection at Rs.7,999 for premium cars.</p>

        <h3>Where are car inspection services available?</h3>
        <p>Across all of Kerala. Our inspector visits the car location directly.</p>

        <h3>How long does a car inspection take?</h3>
        <p>Most inspections are completed within 1-2 hours, with a report delivered shortly after.</p>
      </div>
    </>
  );
}
